import GmapCluster from "vue2-google-maps/dist/components/cluster";
import KBaseMap from "@/components/Maps/KBaseMap";
import KBaseMapInfoWindow from "@/components/Maps/KBaseMapInfoWindow";
import KBaseMapMarker from "@/components/Maps/KBaseMapMarker";
import KServiceStatusIcon from "./KServiceStatusIcon";
import KServicesTable from "@/components/Front/KService/KServicesTable";
import getServiceClusterIcon from "./get-service-cluster-icon";
import getServiceStatusIcon from "./get-service-status-icon";
import { KFullscreenDialog, KPhoneLink, KSelect, KTextField } from "kinnect-general";
import { FetchesData, ToolbarActionable } from "kinnect-mixins";
import { Service, ServiceType } from "kinnect-models";
import {
	VAvatar,
	VBtn,
	VCard,
	VCardActions,
	VCardText,
	VCardTitle,
	VChip,
	VCol,
	VDialog,
	VDivider,
	VIcon,
	VList,
	VListGroup,
	VListItem,
	VListItemAvatar,
	VListItemContent,
	VListItemSubtitle,
	VListItemTitle,
	VOverlay,
	VProgressLinear,
	VSpacer,
	VToolbar,
	VToolbarTitle
} from "vuetify/lib";
import { debounce } from "kinnect-helpers";
import { formatDate } from "kinnect-formatters";
import { gmapApi } from "vue2-google-maps";

export default {
	name: "KServiceMap",

	mixins: [
		FetchesData,
		ToolbarActionable
	],

	data: () => ( {
		cachedServiceIcons: {},
		hiddenServiceTypeIds: [],
		search: "",
		searchServices: [],
		serviceTypes: [],
		services: [],
		serviceStatusUpdateFrom: null,
		serviceStatusUpdateTo: null,
		dialogs: {
			search: { open: false },
			statusConfirm: { open: false },
			statusUpdate: { open: false }
		}
	} ),

	computed: {
		google: gmapApi,

		toolbarActions() {
			if ( this.$can( "edit_services" ) ) {
				return {
					icon: "edit",
					text: "Bulk Update",
					tooltip: "Update service statuses in bulk",
					onClick: () => this.dialogs.statusUpdate.open = true
				};
			}
		},

		clusterStyles() {
			const styles = {};
			const sizes = [
				35,
				45,
				55
			];

			this.serviceTypes.forEach( serviceType => {
				styles[serviceType.name] = sizes.map( size => ( {
					anchor: [ size / 2, size / 2 ],
					height: size,
					textColor: "#FFF",
					url: getServiceClusterIcon( size, serviceType.color ),
					width: size
				} ) );
			} );

			return styles;
		},

		serviceTypeCounts() {
			const counts = {};

			this.services.forEach( service => {
				if ( counts.hasOwnProperty( service.serviceType.name ) ) {
					counts[service.serviceType.name] += 1;
				} else {
					counts[service.serviceType.name] = 1;
				}
			} );

			return counts;
		},

		servicesWithFields() {
			return this.services.map( service => {
				service.fields = [
					{
						avatar: <KServiceStatusIcon
							color={service.serviceType.color}
						></KServiceStatusIcon>,
						title: "Status",
						subTitle: service.serviceType.name
					},
					{
						avatar: <VIcon>person</VIcon>,
						title: "Name",
						subTitle: service.name
					},
					{
						avatar: <VIcon>phone</VIcon>,
						display: Boolean( service.phone ),
						title: "Phone",
						subTitle: <KPhoneLink
							icon={null}
							phone={service.phone}
						></KPhoneLink>
					},
					{
						avatar: <VIcon>email</VIcon>,
						display: Boolean( service.email ),
						title: "Email",
						subTitle: service.email
					},
					{
						avatar: <VIcon>place</VIcon>,
						title: "Address",
						subTitle: `${service.address}, ${service.city}, ${service.state} ${service.zip}`
					},
					{
						avatar: <VIcon>event</VIcon>,
						title: "Purchase Date",
						subTitle: formatDate( service.purchaseDate )
					},
					{
						display: Boolean( service.model ),
						title: "Model",
						subTitle: service.model
					},
					{
						display: Boolean( service.modelNumber ),
						title: "Model Number",
						subTitle: service.modelNumber
					},
					{
						display: Boolean( service.serialNumber ),
						title: "Serial Number",
						subTitle: service.serialNumber
					},
					{
						avatar: <VIcon>comment</VIcon>,
						display: Boolean( service.notes ),
						title: "Notes",
						subTitle: service.notes
					}
				];

				return service;
			} );
		},

		filteredServices() {
			return this.servicesWithFields.filter( service =>
				this.hiddenServiceTypeIds.indexOf( service.serviceType.id ) === -1
			);
		},

		updateFromServiceTypes() {
			const serviceTypes = JSON.parse( JSON.stringify( this.serviceTypes ) );

			return serviceTypes.map( serviceType => {
				serviceType.disabled = !this.serviceTypeCounts[serviceType.name];

				return serviceType;
			} );
		},

		updateToServiceTypes() {
			return this.serviceTypes.filter( serviceType =>
				!this.serviceStatusUpdateFrom || serviceType.id !== this.serviceStatusUpdateFrom.id
			);
		},

		clusteredServices() {
			const services = {};

			if ( this.fetched ) {
				this.serviceTypes.forEach( serviceType => {
					services[serviceType.name] = [];
				} );

				this.filteredServices.forEach( service => {
					services[service.serviceType.name].push( service );
				} );
			}

			return services;
		},

		genLegend() {
			return <VCard style={{
				bottom: "50px",
				left: "10px",
				position: "absolute"
			}}>
				<VList
					class="overflow-y-auto pa-0"
					dense
					maxHeight={400}
					minWidth={220}
				>
					<VListGroup>
						<VListItemTitle slot="activator">Legend</VListItemTitle>

						<VDivider></VDivider>

						{this.serviceTypes.map( serviceType =>
							<VListItem
								key={`legend-${serviceType.id}`}
								onClick={() => this.toggleServiceTypeVis( serviceType.id )}
								style={{
									opacity: this.hiddenServiceTypeIds.indexOf( serviceType.id ) === -1
										? 1
										: 0.5
								}}
							>
								<VListItemAvatar>
									<KServiceStatusIcon
										color={serviceType.color}
									></KServiceStatusIcon>
								</VListItemAvatar>

								<VListItemContent>
									<VListItemTitle>
										{serviceType.name}
										<span class="pl-1">
											({this.serviceTypeCounts[serviceType.name] || 0})
										</span>
									</VListItemTitle>
								</VListItemContent>
							</VListItem>
						)}
					</VListGroup>
				</VList>
			</VCard>;
		},

		genServiceMarkers() {
			return Object.keys( this.clusteredServices ).map( serviceType =>
				<GmapCluster
					batchSize={500}
					batchSizeIE={250}
					gridSize={40}
					maxZoom={15}
					minimumClusterSize={2}
					styles={this.clusterStyles[serviceType]}
					zoomOnClick={true}
				>
					{this.clusteredServices[serviceType].map( service =>
						<KBaseMapMarker
							icon={this.getCachedServiceIcon( service.serviceType.color )}
							lat={service.lat}
							lng={service.lng}
							markerId={service.id}
							markerType="service"
							randomizePosition
							scopedSlots={{
								active: () => this.genInfoWindow( service )
							}}
						></KBaseMapMarker>
					)}
				</GmapCluster>
			);
		},

		genStatusUpdateDialog() {
			return <VDialog
				maxWidth={400}
				vModel={this.dialogs.statusUpdate.open}
			>
				<VCard>
					<VCardTitle>Bulk Status Update</VCardTitle>

					<VCardText>
						<KSelect
							itemText="name"
							itemValue="id"
							items={this.updateFromServiceTypes}
							label="Select All Services with the Status"
							returnObject
							vModel={this.serviceStatusUpdateFrom}
							scopedSlots={{
								item: props => [
									<VListItemAvatar color={props.item.color}></VListItemAvatar>,

									<VListItemContent>
										<VListItemTitle>
											{props.item.name} ({this.serviceTypeCounts[props.item.name] || 0})
										</VListItemTitle>
									</VListItemContent>
								],

								selection: props =>
									<VChip>
										<VAvatar color={props.item.color} left></VAvatar>
										{props.item.name} ({this.serviceTypeCounts[props.item.name] || 0})
									</VChip>
							}}
						></KSelect>

						<KSelect
							itemText="name"
							itemValue="id"
							items={this.updateToServiceTypes}
							label="And Change Their Statuses To"
							returnObject
							vModel={this.serviceStatusUpdateTo}
							scopedSlots={{
								item: props => [
									<VListItemAvatar color={props.item.color}></VListItemAvatar>,

									<VListItemContent>
										<VListItemTitle>
											{props.item.name}
										</VListItemTitle>
									</VListItemContent>
								],

								selection: props =>
									<VChip>
										<VAvatar color={props.item.color} left></VAvatar>
										{props.item.name}
									</VChip>
							}}
						></KSelect>
					</VCardText>

					<VCardActions>
						<VSpacer></VSpacer>

						<VBtn
							onClick={() => this.dialogs.statusUpdate.open = false}
							text
						>Cancel</VBtn>

						<VBtn
							color="link"
							disabled={!this.serviceStatusUpdateFrom || !this.serviceStatusUpdateTo}
							onClick={() => {
								this.dialogs.statusUpdate.open = false;
								this.dialogs.statusConfirm.open = true;
							}}
							text
						>
							<VIcon left>done</VIcon> Bulk Update
						</VBtn>
					</VCardActions>
				</VCard>
			</VDialog>;
		},

		genStatusConfirmDialog() {
			return <VDialog
				maxWidth={400}
				vModel={this.dialogs.statusConfirm.open}
			>
				<VCard color="error" dark>
					<VCardTitle>
						<VIcon left>warning</VIcon> Confirm Bulk Status Update
					</VCardTitle>

					<VCardText>
						Are you sure you want to perform this bulk status update? <strong>This change cannot be undone.</strong>
					</VCardText>

					{this.serviceStatusUpdateFrom
						&& this.serviceStatusUpdateTo
						&& <VCardText class="text-center">
							<div>
								<VChip>
									<VAvatar color={this.serviceStatusUpdateFrom.color} left></VAvatar>
									{this.serviceStatusUpdateFrom.name} ({this.serviceTypeCounts[this.serviceStatusUpdateFrom.name] || 0})
								</VChip>
							</div>

							<div>
								<VIcon>arrow_downward</VIcon>
							</div>

							<div>
								<VChip>
									<VAvatar color={this.serviceStatusUpdateTo.color} left></VAvatar>
									{this.serviceStatusUpdateTo.name}
								</VChip>
							</div>
						</VCardText>
					}

					<VCardActions>
						<VSpacer></VSpacer>

						<VBtn
							onClick={() => this.dialogs.statusConfirm.open = false}
							text
						>Cancel</VBtn>

						<VBtn
							onClick={() => {
								this.dialogs.statusConfirm.open = false;
								this.bulkUpdateStatuses();
							}}
							text
						>
							<VIcon left>done</VIcon> Confirm and Update
						</VBtn>
					</VCardActions>
				</VCard>
			</VDialog>;
		}
	},

	watch: {
		search() {
			this.fetchSearchResults();
		}
	},

	methods: {
		getCachedServiceIcon( color ) {
			if ( !this.cachedServiceIcons.hasOwnProperty( color ) ) {
				this.cachedServiceIcons[color] = getServiceStatusIcon( this.google, 24, color );
			}

			return this.cachedServiceIcons[color];
		},

		toggleServiceTypeVis( idServiceType ) {
			const index = this.hiddenServiceTypeIds.indexOf( idServiceType );

			if ( index === -1 ) {
				this.hiddenServiceTypeIds.push( idServiceType );
			} else {
				this.hiddenServiceTypeIds.splice( index, 1 );
			}
		},

		genInfoWindow( service ) {
			return <KBaseMapInfoWindow
				autoPan
				inactiveOnClose
				offsetX={-2}
				offsetY={-8}
				slot="active"
			>
				<VCard flat light>
					<VList class="list--auto-height">
						{service.fields.map( field => {
							if ( !field.hasOwnProperty( "display" ) || field.display ) {
								const hasTitle = field.hasOwnProperty( "title" );
								const hasSubTitle = field.hasOwnProperty( "subTitle" );
								const hasAvatar = field.hasOwnProperty( "avatar" );

								return <VListItem>
									<VListItemAvatar>
										{hasAvatar && field.avatar}
									</VListItemAvatar>

									{Boolean( hasTitle || hasSubTitle ) && <VListItemContent>
										{hasTitle && <VListItemTitle>
											{field.title}
										</VListItemTitle>}

										{hasSubTitle && <VListItemSubtitle>
											{field.subTitle}
										</VListItemSubtitle>}
									</VListItemContent>}
								</VListItem>;
							}
						} )}
					</VList>

					<VDivider class="mt-4"></VDivider>

					<VCardActions>
						<VSpacer></VSpacer>
						<VBtn
							color="link"
							large
							text
							to={{
								name: "service",
								params: { idService: service.id }
							}}
						>
							<VIcon left>visibility</VIcon> View
						</VBtn>
					</VCardActions>
				</VCard>
			</KBaseMapInfoWindow>;
		},

		async bulkUpdateStatuses() {
			this.$wait.start( "bulk update statuses" );

			try {
				return await this.$http
				.put(
					"services/bulk/service-type",
					{
						idServiceTypeFrom: this.serviceStatusUpdateFrom.id,
						idServiceTypeTo: this.serviceStatusUpdateTo.id
					},
					{ params: { include: "service-type" } }
				)
				.then( ( { data } ) => {
					this.services = data.map( service => new Service( service ) );

					this.$store.commit( "toasts/SHOW", {
						color: "success",
						text: "Successfully saved"
					} );
				} );
			} catch ( e ) {
				let errorMessage = "There was an error when saving. You can try again in a moment or try refreshing the page.";

				if ( e
					&& e.response
					&& e.response.data
					&& e.response.data.message
				) {
					errorMessage = e.response.data.message;
				}

				this.$store.commit( "toasts/SHOW", {
					color: "error",
					text: errorMessage
				} );
			} finally {
				this.$wait.end( "bulk update statuses" );
			}
		},

		fetchSearchResults: debounce( async function() {
			if ( this.search ) {
				const queryWhenCallStarted = this.search;
				const services = await Service
				.custom( "search/services" )
				.params( { query: queryWhenCallStarted } )
				.get();

				if ( this.search === queryWhenCallStarted ) {
					this.searchServices = services;
				}
			} else {
				this.searchServices = [];
			}
		}, 500 ),

		async fetchServiceTypes() {
			this.serviceTypes = await ServiceType.get();
		},

		async fetchServices() {
			this.services = await Service.include( "service-type" ).get();
		},

		async fetch() {
			await this.$http.all( [
				this.fetchServiceTypes(),
				this.fetchServices()
			] );
		}
	},

	render() {
		return <VCol class="d-flex flex-column fill-height pa-0">
			<VToolbar class="shrink" color="primary" dark flat>
				<VToolbarTitle>Service Map</VToolbarTitle>
				<VSpacer></VSpacer>
				<VBtn onClick={() => this.dialogs.search.open = true} text>
					<VIcon left>search</VIcon> Search
				</VBtn>
			</VToolbar>

			<KBaseMap spiderfy storeModuleName="service-map">
				<template slot="visible">
					<VOverlay absolute value={this.$wait.is( "get services" )}>
						Loading...
						<VProgressLinear indeterminate></VProgressLinear>
					</VOverlay>

					<VOverlay absolute value={this.$wait.is( "bulk update statuses" )}>
						Updating...
						<VProgressLinear indeterminate></VProgressLinear>
					</VOverlay>

					{this.genLegend}
				</template>

				{this.genServiceMarkers}

				<KFullscreenDialog
					title="Service Map Search"
					vModel={this.dialogs.search.open}
				>
					<KTextField
						autofocus
						color="white"
						loading={this.$wait.is( "get services" )}
						placeholder="Search"
						prependIcon="search"
						slot="toolbarExtension"
						vModel={this.search}
					></KTextField>

					<KServicesTable
						dense
						items={this.searchServices}
						onGoToMarker={() => this.dialogs.search.open = false}
					></KServicesTable>
				</KFullscreenDialog>
			</KBaseMap>

			{this.genStatusUpdateDialog}
			{this.genStatusConfirmDialog}
		</VCol>;
	}
};
